import { useCallback, useEffect, useMemo, useState } from "react";

import { fetchAPI } from "../../../utils/utils";
import {
    getScheduledNotificationId,
    getUpcomingScheduledNotifications,
} from "../scheduledNotificationsUtils";

function useScheduledNotifications(socket) {
    const [scheduledNotifications, setScheduledNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [cancellationStatus, setCancellationStatus] = useState({});

    const loadScheduledNotifications = useCallback(() => {
        setIsLoading(true);
        fetchAPI('/scheduled-notifications', data => {
            setScheduledNotifications(Array.isArray(data) ? data : []);
            setIsLoading(false);
        }, false, () => {
            setIsLoading(false);
        });
    }, []);

    useEffect(() => {
        loadScheduledNotifications();
    }, [loadScheduledNotifications]);

    useEffect(() => {
        const reload = () => loadScheduledNotifications();
        const handleCancelled = response => {
            setCancellationStatus(prev => ({
                ...prev,
                [response.notificationId]: response.success ? 'success' : 'failed',
            }));

            if (response.success) {
                setScheduledNotifications(prev =>
                    prev.filter(notification => getScheduledNotificationId(notification) !== response.notificationId)
                );
            }
        };

        socket.on('.scheduled_notification_scheduled', reload);
        socket.on('.scheduled_notification_updated', reload);
        socket.on('.scheduled_notification_cancelled', handleCancelled);

        return () => {
            socket.off('.scheduled_notification_scheduled', reload);
            socket.off('.scheduled_notification_updated', reload);
            socket.off('.scheduled_notification_cancelled', handleCancelled);
        };
    }, [socket, loadScheduledNotifications]);

    const cancelScheduledNotification = notificationId => {
        socket.emit('.cancel_scheduled_notification', notificationId);
        setCancellationStatus(prev => ({
            ...prev,
            [notificationId]: 'pending',
        }));
    };

    const upcomingNotifications = useMemo(
        () => getUpcomingScheduledNotifications(scheduledNotifications),
        [scheduledNotifications]
    );

    return {
        cancelScheduledNotification,
        cancellationStatus,
        isLoading,
        loadScheduledNotifications,
        upcomingNotifications,
    };
}

export default useScheduledNotifications;
